import {
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useSecretTokenStore, extractSecretToken } from '@/stores/secretToken';
import type {
  CreateLinkRequest,
  CreateLinkResponse,
  ListLinksResponse,
  LinkActionResponse,
  ApiError,
} from '@/types/api';

const API_BASE_URL = '/api';

class CreateLinkError extends Error {
  constructor(
    message: string,
    public code: string,
    public field?: string
  ) {
    super(message);
    this.name = 'CreateLinkError';
  }
}

async function createLink(
  input: CreateLinkRequest
): Promise<CreateLinkResponse> {
  const response = await fetch(`${API_BASE_URL}/links`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorData = (await response.json()) as ApiError;
    throw new CreateLinkError(
      errorData.error.message,
      errorData.error.code,
      errorData.error.field
    );
  }

  return response.json() as Promise<CreateLinkResponse>;
}

interface UseCreateLinkOptions {
  onSuccess?: (data: CreateLinkResponse) => void;
  onError?: (error: CreateLinkError) => void;
}

export function useCreateLink(
  options?: UseCreateLinkOptions
): ReturnType<
  typeof useMutation<
    CreateLinkResponse,
    CreateLinkError,
    Omit<CreateLinkRequest, 'secretToken'>
  >
> {
  const queryClient = useQueryClient();
  const { secretToken, setSecretToken } = useSecretTokenStore();

  return useMutation({
    mutationFn: async (input: Omit<CreateLinkRequest, 'secretToken'>) => {
      const requestBody: CreateLinkRequest = {
        ...input,
        ...(secretToken && { secretToken }),
      };
      return createLink(requestBody);
    },
    onSuccess: (data) => {
      // Save secretToken from secretUrl if not already saved
      if (!secretToken && data.secretUrl) {
        const token = extractSecretToken(data.secretUrl);
        if (token) {
          setSecretToken(token);
        }
      }

      void queryClient.invalidateQueries({ queryKey: ['links'] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export { CreateLinkError };
export type { CreateLinkRequest, CreateLinkResponse };

class LinkError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'LinkError';
  }
}

interface FetchLinksParams {
  secretToken: string;
  cursor?: string | undefined;
}

async function fetchLinks({
  secretToken,
  cursor,
}: FetchLinksParams): Promise<ListLinksResponse> {
  const params = new URLSearchParams();
  if (cursor) {
    params.set('cursor', cursor);
  }
  params.set('limit', '20');

  const url = `${API_BASE_URL}/manage/${secretToken}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = (await response.json()) as ApiError;
    throw new LinkError(errorData.error.message, errorData.error.code);
  }

  return response.json() as Promise<ListLinksResponse>;
}

export function useListLinks(): ReturnType<
  typeof useInfiniteQuery<ListLinksResponse, LinkError>
> {
  const { secretToken } = useSecretTokenStore();

  return useInfiniteQuery({
    queryKey: ['links', secretToken],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      if (!secretToken) {
        throw new LinkError('Secret token is required', 'MISSING_SECRET_TOKEN');
      }
      return fetchLinks({
        secretToken,
        cursor: pageParam,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!secretToken,
  });
}

async function pauseLink(
  secretToken: string,
  linkId: string
): Promise<LinkActionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/manage/${secretToken}/links/${linkId}/pause`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const errorData = (await response.json()) as ApiError;
    throw new LinkError(errorData.error.message, errorData.error.code);
  }

  return response.json() as Promise<LinkActionResponse>;
}

async function resumeLink(
  secretToken: string,
  linkId: string
): Promise<LinkActionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/manage/${secretToken}/links/${linkId}/resume`,
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const errorData = (await response.json()) as ApiError;
    throw new LinkError(errorData.error.message, errorData.error.code);
  }

  return response.json() as Promise<LinkActionResponse>;
}

interface UseLinkActionOptions {
  onSuccess?: (data: LinkActionResponse) => void;
  onError?: (error: LinkError) => void;
}

export function usePauseLink(
  options?: UseLinkActionOptions
): ReturnType<typeof useMutation<LinkActionResponse, LinkError, string>> {
  const queryClient = useQueryClient();
  const { secretToken } = useSecretTokenStore();

  return useMutation({
    mutationFn: (linkId: string) => {
      if (!secretToken) {
        throw new LinkError('Secret token is required', 'MISSING_SECRET_TOKEN');
      }
      return pauseLink(secretToken, linkId);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['links'] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useResumeLink(
  options?: UseLinkActionOptions
): ReturnType<typeof useMutation<LinkActionResponse, LinkError, string>> {
  const queryClient = useQueryClient();
  const { secretToken } = useSecretTokenStore();

  return useMutation({
    mutationFn: (linkId: string) => {
      if (!secretToken) {
        throw new LinkError('Secret token is required', 'MISSING_SECRET_TOKEN');
      }
      return resumeLink(secretToken, linkId);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['links'] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export { LinkError };
